import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle, AlertTriangle, RefreshCw, Filter, Trash2, Clock } from "lucide-react";
import Paginator from "../../components/Paginator";

const RESULT_COLORS = {
  success: "#10B981",
  duplicate: "#F59E0B",
  bad_signature: "#EF4444",
  user_not_found: "#EF4444",
  invalid_objectid: "#EF4444",
  bad_user_id: "#EF4444",
  bad_amount: "#EF4444",
  zero_amount: "#94A3B8",
};

const colorFor = (r) => {
  if (!r) return "#94A3B8";
  if (RESULT_COLORS[r]) return RESULT_COLORS[r];
  if (r.startsWith("ip_blocked")) return "#EF4444";
  if (r.startsWith("ignored_status_")) return "#94A3B8";
  return "#EF4444";
};

export default function AdminPostbackLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ total: 0, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [source, setSource] = useState("all");
  const [resultFilter, setResultFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [retentionDays, setRetentionDays] = useState(90);

  const updateRetention = async (days) => {
    if (days < retentionDays && days > 0) {
      if (!window.confirm(`Set retention to ${days} days? Logs older than this will be deleted within 1 minute.`)) return;
    }
    try {
      const { data } = await API.put("/admin/postback-logs/retention", { days });
      setRetentionDays(days);
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const purgeNow = async () => {
    const days = parseInt(window.prompt("Purge logs older than how many days?", "30") || "0", 10);
    if (!days || days < 1) return;
    if (!window.confirm(`Permanently delete all postback logs older than ${days} days? This cannot be undone.`)) return;
    try {
      const { data } = await API.post("/admin/postback-logs/purge", { days });
      toast.success(data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/postback-logs", {
        params: { page, per_page: perPage, source, result: resultFilter },
      });
      setLogs(data.logs || []);
      setStats(data.stats || {});
      setPagination({ total: data.total || 0, total_pages: data.total_pages || 1 });
      if (typeof data.retention_days === "number") setRetentionDays(data.retention_days);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, perPage, source, resultFilter]);

  return (
    <AdminLayout title="Postback Logs">
      <div className="space-y-4" data-testid="admin-postback-logs">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { k: "total", label: "Total", color: "#F8FAFC" },
            { k: "success", label: "Success", color: "#10B981" },
            { k: "duplicate", label: "Duplicates", color: "#F59E0B" },
            { k: "failed", label: "Failed/Errors", color: "#EF4444" },
          ].map(({ k, label, color }) => (
            <div key={k} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4">
              <p className="text-[10px] text-[#64748B] uppercase tracking-wider">{label}</p>
              <p className="text-2xl font-mono-game mt-1" style={{ color }}>{stats[k] ?? 0}</p>
            </div>
          ))}
        </div>

        {/* Retention controls */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 flex items-center gap-3 flex-wrap">
          <Clock className="w-4 h-4 text-[#94A3B8]" />
          <div className="flex items-center gap-2">
            <label className="text-[10px] text-[#64748B] uppercase">Auto-delete after</label>
            <select value={retentionDays} onChange={(e) => updateRetention(parseInt(e.target.value, 10))} data-testid="postback-retention-select"
              className="bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 py-1 text-xs">
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>365 days</option>
              <option value={0}>Never expire</option>
            </select>
          </div>
          <span className="text-[10px] text-[#64748B]">
            {retentionDays === 0 ? "Logs are kept forever (manual purge only)" : `Logs older than ${retentionDays} days are auto-purged by MongoDB TTL`}
          </span>
          <button onClick={purgeNow} data-testid="postback-purge-now"
            className="ml-auto px-3 py-1 text-xs font-bold uppercase tracking-wider bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30 rounded-lg hover:bg-[#EF4444]/20 flex items-center gap-1">
            <Trash2 className="w-3 h-3" /> Purge old logs now
          </button>
        </div>

        {/* Filters */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-[#64748B]" />
          <div className="flex items-center gap-2">
            <label className="text-[10px] text-[#64748B] uppercase">Source</label>
            <select value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} data-testid="postback-source-filter"
              className="bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 py-1 text-xs">
              <option value="all">All</option>
              <option value="bitcotasks">Bitcotasks</option>
              <option value="offerwallmedia">OfferwallMedia</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] text-[#64748B] uppercase">Result</label>
            <select value={resultFilter} onChange={(e) => { setResultFilter(e.target.value); setPage(1); }} data-testid="postback-result-filter"
              className="bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 py-1 text-xs">
              <option value="all">All</option>
              <option value="success">Success only</option>
              <option value="duplicate">Duplicates only</option>
              <option value="failed">Failed/errors only</option>
            </select>
          </div>
          <button onClick={load} disabled={loading} data-testid="postback-reload"
            className="ml-auto px-3 py-1 text-xs font-bold uppercase tracking-wider bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 rounded-lg hover:bg-[#00F0FF]/20 disabled:opacity-50 flex items-center gap-1">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Reload
          </button>
        </div>

        {/* Logs table */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 overflow-hidden">
          <div className="px-4 py-3 border-b border-[#2A3241]/30 flex items-center justify-between">
            <p className="text-sm font-bold text-[#F8FAFC]">Postback Events</p>
            <p className="text-xs text-[#64748B]">{pagination.total.toLocaleString()} matching</p>
          </div>
          <div className="divide-y divide-[#2A3241]/15 max-h-[60vh] overflow-y-auto">
            {logs.length === 0 && (
              <p className="text-xs text-[#64748B] text-center py-8">No postback events match current filters.</p>
            )}
            {logs.map((log, i) => (
              <div key={`${log.source}-${log.txn_id || i}-${log.ts}`} className="p-3 hover:bg-[#0B0E14]/40 transition-colors" data-testid={`postback-row-${i}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${log.source === "bitcotasks" ? "bg-[#FFD600]/10 text-[#FFD600]" : "bg-[#B026FF]/10 text-[#B026FF]"}`}>{log.source}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider" style={{ backgroundColor: `${colorFor(log.result)}1a`, color: colorFor(log.result) }}>
                      {log.result === "success" ? <CheckCircle className="w-2.5 h-2.5 inline -mt-0.5" /> : <AlertTriangle className="w-2.5 h-2.5 inline -mt-0.5" />}
                      {" "}{log.result}
                    </span>
                    {log.username && <span className="text-xs text-[#F8FAFC] truncate max-w-[180px]">{log.username}</span>}
                    {log.coins_credited > 0 && <span className="text-xs font-mono-game text-[#10B981]">+{log.coins_credited}c</span>}
                  </div>
                  <span className="text-[10px] text-[#64748B] font-mono-game shrink-0">{log.ts ? new Date(log.ts).toLocaleString() : ""}</span>
                </div>
                <div className="mt-1 flex items-center gap-3 text-[10px] text-[#64748B] font-mono-game flex-wrap">
                  {log.txn_id && <span>txn: <span className="text-[#94A3B8]">{log.txn_id}</span></span>}
                  {log.user_id && <span>uid: <span className="text-[#94A3B8]">{(log.user_id || "").slice(0, 12)}...</span></span>}
                  {log.reward_raw && <span>reward_raw: <span className="text-[#94A3B8]">{log.reward_raw}</span></span>}
                  {log.ip && <span>ip: <span className="text-[#94A3B8]">{log.ip}</span></span>}
                  {log.status_param !== undefined && log.status_param !== "" && <span>status: <span className="text-[#94A3B8]">{log.status_param}</span></span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <Paginator page={page} totalPages={pagination.total_pages} total={pagination.total}
          perPage={perPage} onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
          onPageChange={setPage} testIdPrefix="postback-pager" />
      </div>
    </AdminLayout>
  );
}
